import type NotificationType from '@/enum/NotificationType'
import SignalRGroupType from '@/enum/SignalRGroupType'
import { MainToWorkerMessageType, WorkerToMainMessageType } from '@/enum/SignalRWorkerMessageType'
import { WORKER_SIGNAL_R } from '@/utils/constants'
import { logInfo } from '@/utils/helpers/logger'
import { defineStore } from 'pinia'
import useGlobalStore from '../global'
import useJobsStore from '../jobs'
import useWorkersStore from '../workers'
import state from './state'

const useRealtimeStore = defineStore('realtimeStore', {
  state: () => state,
  actions: {
    handleSignalRConnectionStatus(status: NotificationType, message: string) {
      logInfo(message)
    },

    async setupSignalRWorker(signalRworker: any) {
      const globalStore = useGlobalStore()
      const workersStore = useWorkersStore()

      const worker = new signalRworker()
      worker.postMessage({
        name: MainToWorkerMessageType.Connect,
        accessToken: globalStore.accessToken
      })
    
      worker.onmessage = (event: any) => {
        const { name, ...rest } = event.data
        switch (name) {
          case WorkerToMainMessageType.Connected: {
            workersStore.saveWorker(WORKER_SIGNAL_R, worker)
            this.handleSignalRConnectionStatus(rest.status, rest.message)
            break
          }
          default:
            break
        }
      }
    },

    async subscribeToJobActivityUpdates(signalRWorker: any, navigator: any) {
      if (!signalRWorker || this.jobActivitiesSubscribed) return
      
      const jobsStore = useJobsStore()

      signalRWorker.postMessage({
        name: MainToWorkerMessageType.Subscribe,
        route: navigator?.getCurrentRoute().name,
        channels: [
          SignalRGroupType.JobActivity, SignalRGroupType.JobUpdateChannel, SignalRGroupType.JobUpdate
        ]
      })

      signalRWorker.onmessage = (event: any) => {
        const { name, ...rest } = event.data
        switch (name) {
          case WorkerToMainMessageType.Subscribed:
            break
          case WorkerToMainMessageType.UpdateJobActivity: 
          case WorkerToMainMessageType.UpdateJob:
          case WorkerToMainMessageType.UpdateJobDetails:
            jobsStore.handleJobRealtimeUpdate(name, rest.data, navigator)
            break
          default:
            break
        }
      }

      this.jobActivitiesSubscribed = true
    },

    async subscribeToJobActivityAndDetailsUpdates(signalRWorker: any, navigator: any) {
      
      if (!signalRWorker) return
      this.subscribedJobId = navigator.getCurrentRoute()?.params.id
      const jobsStore = useJobsStore()

      signalRWorker.postMessage({
        name: MainToWorkerMessageType.SubscribeGroupById,
        route: navigator?.getCurrentRoute().name,
        channels: [
          SignalRGroupType.JobActivity, this.subscribedJobId
        ]
      })

      signalRWorker.onmessage = (event: any) => {
        const { name, ...rest } = event.data
        switch (name) {
          case WorkerToMainMessageType.UpdateJobDetails:
          case WorkerToMainMessageType.UpdateJobActivity: 
          case WorkerToMainMessageType.UpdateJob:
            jobsStore.handleJobRealtimeUpdate(name, rest.data, navigator)
            break
          default:
            break
        }
      }

      this.jobActivitiesSubscribed = true
    },

    async unsubscribeToJobDetailsUpdates(signalRWorker: any, navigator: any) {
      if (!signalRWorker) return
      const jobId = this.subscribedJobId ?? navigator.getCurrentRoute()?.params.id

      if (jobId) {
        signalRWorker.postMessage({
          name: MainToWorkerMessageType.UnsubscribeGroup,
          route: navigator?.getCurrentRoute().name,
          channels: [
            jobId
          ],
          groupId: jobId
        })
      }

      this.subscribedJobId = null
    },

    async unsubscribeToJobActivityUpdates(signalRWorker: any, navigator: any) {
      if (!signalRWorker) return

      signalRWorker.postMessage({
        name: MainToWorkerMessageType.Unsubscribe,
        route: navigator?.getCurrentRoute().name,
        channels: [
          SignalRGroupType.JobActivity
        ],
      })

      this.jobActivitiesSubscribed = false
    }
  }
})
export default useRealtimeStore
